<script setup>
// T-259 · A CORTINA COM AS CONVERSAS POR TRÁS DE UM NÚMERO.
//
// O padrão nasceu na Jornada do Torcedor e foi copiado à mão na Visão Geral (T-170). Esta é a
// terceira tela a precisar dele, e copiar de novo garantiria três drills com comportamentos
// ligeiramente diferentes: tela que abre detalhe não pode parecer um lugar diferente do resto. Daqui
// para frente, drill novo usa este componente.
//
// Abre em aba nova, sempre. Quem clica está lendo um relatório e quer ver um caso sem perder o
// número que o levou até ele.
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { frontendURL } from 'dashboard/helper/URLHelper';

const props = defineProps({
  titulo: { type: String, required: true },
  subtitulo: { type: String, default: '' },
  conversas: { type: Array, default: () => [] },
  carregando: { type: Boolean, default: false },
  // Quando a lista foi cortada, a cortina diz quantas existem de verdade: sem isso quem clica em
  // "40 horas" lê 100 casos e acha que são todos.
  total: { type: Number, default: null },
});

defineEmits(['fechar']);

const { t } = useI18n();
const route = useRoute();

const cortada = computed(
  () => props.total !== null && props.total > props.conversas.length
);

const urlDaConversa = linha =>
  frontendURL(
    `accounts/${route.params.accountId}/conversations/${linha.display_id}`
  );

const dataCurta = ts => {
  if (!ts) return '';
  return new Date(ts * 1000).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });
};
</script>

<template>
  <div class="fixed inset-0 z-[80]">
    <div class="absolute inset-0 bg-black/30" @click="$emit('fechar')" />
    <div
      class="absolute inset-y-0 right-0 flex flex-col w-full max-w-md border-l shadow-xl bg-n-solid-1 border-n-weak"
    >
      <div
        class="flex items-start justify-between gap-3 px-5 py-3 border-b border-n-weak"
      >
        <div class="min-w-0">
          <h3 class="text-base font-semibold text-n-slate-12">{{ titulo }}</h3>
          <p v-if="subtitulo" class="text-xs text-n-slate-11">
            {{ subtitulo }}
          </p>
          <p v-if="cortada" class="text-xs text-n-amber-11">
            {{ t('CORTINA_CONVERSAS.CORTADA', { mostradas: conversas.length, total }) }}
          </p>
        </div>
        <button
          class="p-1 text-n-slate-11 hover:text-n-slate-12"
          :aria-label="t('CORTINA_CONVERSAS.FECHAR')"
          @click="$emit('fechar')"
        >
          <span class="i-lucide-x size-5" />
        </button>
      </div>

      <div class="flex-1 overflow-auto">
        <div v-if="carregando" class="p-6 text-sm text-n-slate-11">
          {{ t('CORTINA_CONVERSAS.CARREGANDO') }}
        </div>
        <div
          v-else-if="!conversas.length"
          class="p-6 text-sm text-n-slate-11"
        >
          {{ t('CORTINA_CONVERSAS.VAZIO') }}
        </div>
        <a
          v-for="linha in conversas"
          v-else
          :key="linha.display_id"
          :href="urlDaConversa(linha)"
          target="_blank"
          rel="noopener"
          class="flex items-center justify-between gap-3 px-5 py-3 border-b border-n-weak hover:bg-n-alpha-1"
        >
          <span class="min-w-0">
            <span class="block text-sm font-medium truncate text-n-slate-12">
              {{ linha.contato || t('CORTINA_CONVERSAS.SEM_CONTATO') }}
              <!-- O ícone diz que sai da tela, antes do clique. Sem ele, abrir aba nova é
                   surpresa, e surpresa em relatório faz a pessoa perder o lugar onde estava. -->
              <span
                class="inline-block align-middle i-lucide-external-link size-3 text-n-slate-10"
              />
            </span>
            <span class="block text-xs text-n-slate-11">
              #{{ linha.display_id }}
              <template v-if="linha.label"> · {{ linha.label }}</template>
              <template v-if="linha.encerrado_em">
                · {{ dataCurta(linha.encerrado_em) }}</template
              >
            </span>
          </span>
          <slot name="direita" :linha="linha" />
        </a>
      </div>
    </div>
  </div>
</template>
